import React, { useState, useEffect, useCallback } from "react";
import {
  getAllFeeHeads,
  getAllFeeHeadsPortal,
  deleteFeeHead,
  deleteFeeHeadPortal
} from "../../api/fees/feeApi";
import FeeHeadModal from "../../components/fees/FeeHeadModal";
import {
  Button,
  Input,
  Badge,
  Table,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  Plus,
  Search,
  Layers
} from "lucide-react";
import {
  useToast
} from "../../context/ToastContext";

const FeeHeadList = ({ isPortal = false }) => {
  const [heads, setHeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await getAllFeeHeadsPortal() : await getAllFeeHeads();
      setHeads(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load fee heads.");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this fee head?")) return;
    setDeletingId(id);
    try {
      if (isPortal) await deleteFeeHeadPortal(id);
      else await deleteFeeHead(id);
      toast.success("Fee head deactivated successfully.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to deactivate fee head.");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = heads.filter((h) => {
    const q = search.toLowerCase();
    return (
      (h.name || "").toLowerCase().includes(q) ||
      (h.code || "").toLowerCase().includes(q) ||
      (h.type || "").toLowerCase().includes(q) ||
      (h.frequency || "").toLowerCase().includes(q) ||
      (h.source || "").toLowerCase().includes(q)
    );
  });

  const formatSource = (h) => {
    if (h.type === "TRANSPORT" || h.source === "TRANSPORT_MODULE") return "Transport module";
    if (h.source === "MANUAL" || !h.source) return "Manual";
    return h.source;
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      render: (item) => {
        const isTransport =
          item.type === "TRANSPORT" || item.source === "TRANSPORT_MODULE";
        return (
          <span className="font-medium text-neutral-900 dark:text-neutral-100">
            {item.name}
            {isTransport && (
              <Badge variant="warning" className="ml-2">
                Transport
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      key: "code",
      header: "Code",
      render: (item) => (
        <span className="font-mono text-xs text-neutral-600 dark:text-neutral-300">{item.code}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (item) => (item.type || "").replace(/_/g, " "),
    },
    {
      key: "frequency",
      header: "Frequency",
      render: (item) => item.frequency || "—",
    },
    {
      key: "defaultAmount",
      header: "Default amount",
      render: (item) => {
        const isTransport =
          item.type === "TRANSPORT" || item.source === "TRANSPORT_MODULE";
        return isTransport
          ? "—"
          : `₹${Number(item.defaultAmount || 0).toFixed(2)}`;
      },
    },
    {
      key: "source",
      header: "Source",
      render: (item) => formatSource(item),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <Badge variant={item.isActive ? "success" : "default"}>
          {item.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (item) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditData(item);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          {item.isActive && (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger-500 hover:text-danger-700"
              disabled={deletingId === item._id}
              isLoading={deletingId === item._id}
              onClick={() => handleDelete(item._id)}
            >
              Deactivate
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Fee heads</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Categories, billing frequency, default amounts, and data sources.
          </p>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditData(null);
            setModalOpen(true);
          }}
        >
          Add fee head
        </Button>
      </div>

      
      

      <Input
        placeholder="Search by name, code, type, frequency, or source…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        leftIcon={<Search className="h-4 w-4" />}
        className="max-w-md"
      />

      {loading ? (
        <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          <div className="flex gap-4">
            <Skeleton height={12} width="20%" />
            <Skeleton height={12} width="25%" />
            <Skeleton height={12} width="15%" />
            <Skeleton height={12} width="20%" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="20%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
              <Skeleton height={16} width="20%" />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          data={filtered}
          emptyState={
            <EmptyState
              icon={<Layers />}
              title="No fee heads found"
              description={
                search
                  ? "Try adjusting your search criteria."
                  : "Create a fee head to start defining fee categories."
              }
              action={
                !search && (
                  <Button
                    leftIcon={<Plus className="h-4 w-4" />}
                    onClick={() => {
                      setEditData(null);
                      setModalOpen(true);
                    }}
                  >
                    Add fee head
                  </Button>
                )
              }
            />
          }
        />
      )}

      <FeeHeadModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          toast.success(editData ? "Fee head updated successfully." : "Fee head created successfully.");
          fetchData();
        }}
        editData={editData}
        isPortal={isPortal}
      />
    </div>
  );
};

export default FeeHeadList;
